import React, { useState, useEffect } from 'react';
import {
  Box, Card, CardContent, Typography, Grid, Button, Chip, Alert, LinearProgress,
  List, ListItem, ListItemIcon, ListItemText, Divider, IconButton, Tooltip,
  Dialog, DialogTitle, DialogContent, DialogActions, TextField, FormControl,
  InputLabel, Select, MenuItem, Switch, FormControlLabel, Tabs, Tab,
  Timeline, TimelineItem, TimelineSeparator, TimelineConnector, TimelineContent,
  TimelineDot, TimelineOppositeContent, Accordion, AccordionSummary, AccordionDetails
} from '@mui/material';
import {
  AutoAwesome, Schedule, Email, Phone, LinkedIn, Sms, TrendingUp, 
  CheckCircle, Warning, Info, Star, StarBorder, Timeline as TimelineIcon,
  Analytics, SmartToy, DataUsage, Insights, Target, Notifications,
  Settings, PlayArrow, Pause, Stop, Refresh, Add, Edit, Delete,
  ExpandMore, Send, ScheduleSend, Done, Error, Pending
} from '@mui/icons-material';

const SmartFollowUpSystem = () => {
  const [activeTab, setActiveTab] = useState(0);
  const [sequences, setSequences] = useState([]);
  const [activeCampaigns, setActiveCampaigns] = useState([]);
  const [showCreateSequence, setShowCreateSequence] = useState(false);
  const [showSettings, setShowSettings] = useState(false);

  // Mock data for sequences
  const mockSequences = [
    {
      id: 1,
      name: 'High-Value Lead Sequence',
      description: 'Multi-channel follow-up for leads with 80%+ conversion probability',
      status: 'active',
      channels: ['email', 'linkedin', 'phone'],
      steps: [
        {
          id: 1,
          channel: 'email',
          delay: 0,
          template: 'Initial Outreach',
          content: 'Hi {{name}}, I noticed {{company}} is growing rapidly. Would love to discuss how we can help scale your {{pain_point}}...',
          subject: 'Quick question about {{company}} growth'
        },
        {
          id: 2,
          channel: 'linkedin',
          delay: 2,
          template: 'Connection Request',
          content: 'Hi {{name}}, I enjoyed your recent post about {{interest}}. Would love to connect and share some insights about {{pain_point}}...'
        },
        {
          id: 3,
          channel: 'email',
          delay: 5,
          template: 'Value Proposition',
          content: 'Hi {{name}}, Following up on my previous message. I put together a quick analysis of how {{company}} could improve {{pain_point}}...',
          subject: 'Quick analysis for {{company}}'
        },
        {
          id: 4,
          channel: 'phone',
          delay: 7,
          template: 'Phone Call',
          content: 'Call script: Hi {{name}}, this is [Your Name] from [Company]. I sent you a few emails about {{pain_point}}...'
        }
      ],
      triggers: ['conversion_probability > 80', 'deal_size > $25K'],
      performance: {
        openRate: 78.5,
        responseRate: 23.4,
        conversionRate: 12.8,
        avgResponseTime: '2.3 hours'
      }
    },
    {
      id: 2,
      name: 'Warm Lead Nurture',
      description: 'Gentle nurturing sequence for leads in research phase',
      status: 'active',
      channels: ['email', 'linkedin'],
      steps: [
        {
          id: 1,
          channel: 'email',
          delay: 0,
          template: 'Welcome',
          content: 'Hi {{name}}, thanks for your interest in {{solution}}. Here\'s a helpful resource about {{pain_point}}...',
          subject: 'Welcome to {{company}} - Here\'s your resource'
        },
        {
          id: 2,
          channel: 'email',
          delay: 3,
          template: 'Educational Content',
          content: 'Hi {{name}}, I thought you might find this case study interesting. It shows how we helped {{similar_company}} with {{pain_point}}...',
          subject: 'Case Study: How {{similar_company}} solved {{pain_point}}'
        },
        {
          id: 3,
          channel: 'linkedin',
          delay: 7,
          template: 'Engagement',
          content: 'Hi {{name}}, I shared a post about {{pain_point}} that might be relevant to {{company}}. Would love your thoughts!'
        }
      ],
      triggers: ['conversion_probability 40-80', 'deal_size $10K-25K'],
      performance: {
        openRate: 65.2,
        responseRate: 15.7,
        conversionRate: 8.3,
        avgResponseTime: '1.8 days'
      }
    }
  ];

  // Mock active campaigns
  const mockActiveCampaigns = [
    {
      id: 1,
      sequenceId: 1,
      leadId: 1,
      leadName: 'Sarah Johnson',
      company: 'TechStart Inc',
      status: 'in_progress',
      currentStep: 2,
      totalSteps: 4,
      startDate: '2024-01-15T09:00:00Z',
      nextAction: '2024-01-17T14:00:00Z',
      progress: 50,
      responses: [
        { date: '2024-01-15T10:30:00Z', channel: 'email', type: 'open' },
        { date: '2024-01-15T11:15:00Z', channel: 'email', type: 'click' }
      ]
    },
    {
      id: 2,
      sequenceId: 2,
      leadId: 2,
      leadName: 'Mike Chen',
      company: 'GrowthCo',
      status: 'completed',
      currentStep: 3,
      totalSteps: 3,
      startDate: '2024-01-14T08:00:00Z',
      nextAction: null,
      progress: 100,
      responses: [
        { date: '2024-01-14T09:30:00Z', channel: 'email', type: 'open' },
        { date: '2024-01-14T10:45:00Z', channel: 'email', type: 'reply' },
        { date: '2024-01-16T14:20:00Z', channel: 'linkedin', type: 'connection_accepted' }
      ]
    }
  ];

  useEffect(() => {
    setSequences(mockSequences);
    setActiveCampaigns(mockActiveCampaigns);
  }, [mockActiveCampaigns, mockSequences]);

  const getStatusColor = (status) => {
    switch (status) {
      case 'active': return 'success';
      case 'paused': return 'warning';
      case 'completed': return 'info';
      case 'error': return 'error';
      default: return 'default';
    }
  };

  const getChannelIcon = (channel) => {
    switch (channel) {
      case 'email': return <Email />;
      case 'linkedin': return <LinkedIn />;
      case 'phone': return <Phone />;
      case 'sms': return <Sms />;
      default: return <Email />;
    }
  };

  const renderSequences = () => (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h6">Follow-up Sequences</Typography>
        <Button
          variant="contained"
          startIcon={<Add />}
          onClick={() => setShowCreateSequence(true)}
        >
          Create Sequence
        </Button>
      </Box>

      <Grid container spacing={3}>
        {sequences.map((sequence) => (
          <Grid item xs={12} md={6} key={sequence.id}>
            <Card>
              <CardContent>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
                  <Box>
                    <Typography variant="h6">{sequence.name}</Typography>
                    <Typography variant="body2" color="text.secondary">
                      {sequence.description}
                    </Typography>
                  </Box>
                  <Chip 
                    label={sequence.status} 
                    color={getStatusColor(sequence.status)}
                    size="small"
                  />
                </Box>

                <Box sx={{ mb: 2 }}>
                  <Typography variant="body2" color="text.secondary" gutterBottom>
                    Channels: {sequence.channels.map(ch => ch.charAt(0).toUpperCase() + ch.slice(1)).join(', ')}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Steps: {sequence.steps.length} • Triggers: {sequence.triggers.length}
                  </Typography>
                </Box>

                <Box sx={{ mb: 2 }}>
                  <Typography variant="subtitle2" gutterBottom>Performance</Typography>
                  <Grid container spacing={1}>
                    <Grid item xs={6}>
                      <Typography variant="body2" color="text.secondary">Open Rate</Typography>
                      <Typography variant="h6" color="primary">{sequence.performance.openRate}%</Typography>
                    </Grid>
                    <Grid item xs={6}>
                      <Typography variant="body2" color="text.secondary">Response Rate</Typography>
                      <Typography variant="h6" color="success.main">{sequence.performance.responseRate}%</Typography>
                    </Grid>
                    <Grid item xs={6}>
                      <Typography variant="body2" color="text.secondary">Conversion</Typography>
                      <Typography variant="h6" color="warning.main">{sequence.performance.conversionRate}%</Typography>
                    </Grid>
                    <Grid item xs={6}>
                      <Typography variant="body2" color="text.secondary">Avg Response</Typography>
                      <Typography variant="h6" color="info.main">{sequence.performance.avgResponseTime}</Typography>
                    </Grid>
                  </Grid>
                </Box>

                <Box sx={{ display: 'flex', gap: 1 }}>
                  <Button size="small" variant="outlined" startIcon={<Edit />}>
                    Edit
                  </Button>
                  <Button size="small" variant="outlined" startIcon={<Analytics />}>
                    Analytics
                  </Button>
                  <Button size="small" variant="outlined" startIcon={<PlayArrow />}>
                    {sequence.status === 'active' ? 'Pause' : 'Activate'}
                  </Button>
                </Box>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>
    </Box>
  );

  const renderActiveCampaigns = () => (
    <Box>
      <Typography variant="h6" gutterBottom>Active Campaigns</Typography>
      <List>
        {activeCampaigns.map((campaign, index) => (
          <React.Fragment key={campaign.id}>
            <ListItem>
              <ListItemIcon>
                <TimelineIcon color="primary" />
              </ListItemIcon>
              <ListItemText
                primary={
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Typography variant="subtitle1">{campaign.leadName}</Typography>
                    <Chip 
                      label={campaign.status.replace('_', ' ')} 
                      size="small" 
                      color={getStatusColor(campaign.status)}
                    />
                    <Chip 
                      label={`Step ${campaign.currentStep}/${campaign.totalSteps}`} 
                      size="small" 
                      variant="outlined"
                    />
                    <Chip 
                      label={campaign.company} 
                      size="small" 
                      variant="outlined"
                    />
                  </Box>
                }
                secondary={
                  <Box>
                    <Typography variant="body2" color="text.secondary">
                      Started: {new Date(campaign.startDate).toLocaleDateString()}
                      {campaign.nextAction && ` • Next: ${new Date(campaign.nextAction).toLocaleDateString()}`}
                    </Typography>
                    <Box sx={{ mt: 1 }}>
                      <LinearProgress 
                        variant="determinate" 
                        value={campaign.progress} 
                        sx={{ height: 6, borderRadius: 3 }}
                      />
                    </Box>
                    <Box sx={{ display: 'flex', gap: 1, mt: 1 }}>
                      {campaign.responses.map((response, idx) => (
                        <Chip
                          key={idx}
                          icon={getChannelIcon(response.channel)}
                          label={response.type}
                          size="small"
                          variant="outlined"
                        />
                      ))}
                    </Box>
                  </Box>
                }
              />
              <Box>
                <Tooltip title="View Timeline">
                  <IconButton size="small">
                    <TimelineIcon />
                  </IconButton>
                </Tooltip>
                <Tooltip title="Manual Override">
                  <IconButton size="small">
                    <Edit />
                  </IconButton>
                </Tooltip>
              </Box>
            </ListItem>
            {index < activeCampaigns.length - 1 && <Divider />}
          </React.Fragment>
        ))}
      </List>
    </Box>
  );

  const renderAnalytics = () => (
    <Box>
      <Typography variant="h6" gutterBottom>Follow-up Analytics</Typography>
      <Grid container spacing={2}>
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>Channel Performance</Typography>
              <List>
                <ListItem>
                  <ListItemIcon><Email color="primary" /></ListItemIcon>
                  <ListItemText
                    primary="Email"
                    secondary="Open Rate: 72.3% • Response Rate: 18.7%"
                  />
                  <Chip label="Primary" color="success" size="small" />
                </ListItem>
                <ListItem>
                  <ListItemIcon><LinkedIn color="primary" /></ListItemIcon>
                  <ListItemText
                    primary="LinkedIn"
                    secondary="Connection Rate: 45.2% • Response Rate: 12.3%"
                  />
                  <Chip label="Secondary" color="warning" size="small" />
                </ListItem>
                <ListItem>
                  <ListItemIcon><Phone color="primary" /></ListItemIcon>
                  <ListItemText
                    primary="Phone"
                    secondary="Answer Rate: 23.1% • Conversion Rate: 8.9%"
                  />
                  <Chip label="Tertiary" color="info" size="small" />
                </ListItem>
              </List>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>Timing Insights</Typography>
              <Box sx={{ mb: 2 }}>
                <Typography variant="body2" color="text.secondary">Best Response Times</Typography>
                <Typography variant="h6" color="primary">Tuesday 2-4 PM</Typography>
                <Typography variant="body2" color="text.secondary">Average response time: 1.2 hours</Typography>
              </Box>
              <Box sx={{ mb: 2 }}>
                <Typography variant="body2" color="text.secondary">Worst Response Times</Typography>
                <Typography variant="h6" color="error">Friday 5-7 PM</Typography>
                <Typography variant="body2" color="text.secondary">Average response time: 3.8 days</Typography>
              </Box>
              <Box>
                <Typography variant="body2" color="text.secondary">Optimal Sequence Length</Typography>
                <Typography variant="h6" color="success.main">4-6 steps</Typography>
                <Typography variant="body2" color="text.secondary">Peak engagement at step 3</Typography>
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Box>
  );

  return (
    <Box sx={{ p: { xs: 0.25, sm: 1 }, pb: { xs: 2, sm: 3 } }}>
      <Typography variant="h4" gutterBottom>Smart Follow-up System</Typography>
      <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
        Automated multi-channel follow-up sequences with AI-powered timing and personalization
      </Typography>

      <Alert severity="info" sx={{ mb: 3 }}>
        <Typography variant="body2">
          <strong>AI-Powered Follow-ups:</strong> Our system automatically sends personalized messages 
          at optimal times across multiple channels, with intelligent sequencing based on lead behavior.
        </Typography>
      </Alert>

      <Tabs value={activeTab} onChange={(e, newValue) => setActiveTab(newValue)} sx={{ mb: 3 }}>
        <Tab label="Sequences" />
        <Tab label="Active Campaigns" />
        <Tab label="Analytics" />
      </Tabs>

      {activeTab === 0 && renderSequences()}
      {activeTab === 1 && renderActiveCampaigns()}
      {activeTab === 2 && renderAnalytics()}
    </Box>
  );
};

export default SmartFollowUpSystem;
